import { pageZoom } from './zoom.js';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { Icon } from './Icon.js';
import { BottomSheet } from './Overlay.js';
import { TextField, type FieldFrameProps } from './TextField.js';
import { iconCheck } from './generated/icons.js';
import { SIDE_SHEET_QUERY, cx, useControllable, useMediaQuery } from './util.js';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** What `onChange` hands over: the chosen value, shaped like the event of a native select. */
export interface SelectChange {
  target: { value: string; name: string | undefined };
}

export interface SelectFieldProps extends FieldFrameProps {
  options: SelectOption[];
  /** Shown while nothing is chosen. */
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (event: SelectChange) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  /** Labels for the phone sheet. */
  closeLabel?: string;
  searchLabel?: string;
}

/** Past this many options the phone sheet gets a search field. */
const SEARCH_FROM = 8;
const MENU_MAX = 320;

/**
 * A select drawn in the design system. On a wide window it opens a menu under the field
 * (listbox pattern: arrows, Home, End, type ahead, Enter, Escape); on a phone it opens a bottom
 * sheet of radio rows. Its props are those of the old native select, and `onChange` gets the
 * chosen value in `event.target.value`.
 */
export function SelectField({
  label,
  supporting,
  error,
  className,
  id,
  options,
  placeholder,
  value,
  defaultValue = '',
  onChange,
  disabled = false,
  required = false,
  name,
  closeLabel = 'Close',
  searchLabel = 'Search',
}: SelectFieldProps) {
  const auto = useId();
  const buttonId = id ?? `${auto}-button`;
  const labelId = `${auto}-label`;
  const listId = `${auto}-list`;
  const noteId = `${auto}-note`;
  const [current, setCurrent] = useControllable<string>(value, defaultValue);
  const wide = useMediaQuery(SIDE_SHEET_QUERY);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<CSSProperties>({});
  const [query, setQuery] = useState('');
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const chosen = options.find((o) => o.value === current);
  const hasNote = error !== undefined || supporting !== undefined;

  const choose = (next: string) => {
    setCurrent(next);
    onChange?.({ target: { value: next, name } });
    setOpen(false);
    setQuery('');
    button.current?.focus();
  };
  const close = () => {
    setOpen(false);
    setQuery('');
    button.current?.focus();
  };
  const enabled = (index: number) =>
    options[index] !== undefined && options[index]?.disabled !== true;
  const step = (from: number, direction: 1 | -1): number => {
    for (let i = from + direction; i >= 0 && i < options.length; i += direction) {
      if (enabled(i)) return i;
    }
    return from;
  };
  const openMenu = () => {
    if (disabled) return;
    const at = options.findIndex((o) => o.value === current);
    setActive(at >= 0 ? at : step(-1, 1));
    setOpen(true);
  };

  // Where the menu goes: under the field, or above it when there is no room below. It is fixed,
  // so a sheet or a card around the field never clips it.
  useLayoutEffect(() => {
    if (!open || !wide || button.current === null) return;
    const z = pageZoom();
    const r = button.current.getBoundingClientRect();
    const rect = { left: r.left / z, top: r.top / z, bottom: r.bottom / z, width: r.width / z };
    const viewH = window.innerHeight / z;
    const below = viewH - rect.bottom;
    const up = below < Math.min(MENU_MAX, options.length * 48) && rect.top > below;
    setPlace({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(160, Math.min(MENU_MAX, (up ? rect.top : below) - 16)),
      ...(up ? { bottom: viewH - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, [open, wide, options.length]);
  useEffect(() => {
    if (!open || !wide) return;
    list.current?.focus();
    const away = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!list.current?.contains(target) && !button.current?.contains(target)) setOpen(false);
    };
    const leave = (event: Event) => {
      if (!list.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    window.addEventListener('resize', leave);
    window.addEventListener('scroll', leave, true);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('resize', leave);
      window.removeEventListener('scroll', leave, true);
    };
  }, [open, wide]);
  useEffect(() => {
    if (open && wide)
      document
        .getElementById(`${listId}-${String(active)}`)
        ?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open, wide, listId]);

  const onButtonKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      openMenu();
    }
  };
  const onListKey = (event: KeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive(step(active, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActive(step(active, -1));
        return;
      case 'Home':
        event.preventDefault();
        setActive(step(-1, 1));
        return;
      case 'End':
        event.preventDefault();
        setActive(step(options.length, -1));
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (enabled(active)) choose(options[active]?.value ?? '');
        return;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      case 'Tab':
        setOpen(false);
        return;
      default:
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
      const now = Date.now();
      const buffer = now - typed.current.at > 600 ? event.key : typed.current.text + event.key;
      typed.current = { text: buffer, at: now };
      const from = buffer.length === 1 ? active + 1 : active;
      const hit = [...options.keys()]
        .map((i) => (i + from) % options.length)
        .find(
          (i) => enabled(i) && options[i]?.label.toLowerCase().startsWith(buffer.toLowerCase()),
        );
      if (hit !== undefined) setActive(hit);
    }
  };

  const shown = useMemo(
    () =>
      query.trim() === ''
        ? options
        : options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase())),
    [options, query],
  );

  return (
    <div className={cx('k-field', error !== undefined && 'k-field--error', className)}>
      <label id={labelId} htmlFor={buttonId} className="k-field__label k-t-label-sm">
        {label}
      </label>
      <div className="k-field__box k-field__box--select">
        <button
          ref={button}
          id={buttonId}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open && wide ? listId : undefined}
          aria-labelledby={`${labelId} ${buttonId}`}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={hasNote ? noteId : undefined}
          aria-required={required || undefined}
          disabled={disabled}
          className="k-field__input k-select__button k-t-body-lg"
          onClick={() => (open ? close() : openMenu())}
          onKeyDown={onButtonKey}
        >
          <span className={cx(chosen === undefined && 'k-muted')}>
            {chosen?.label ?? placeholder ?? ' '}
          </span>
        </button>
      </div>
      {hasNote ? (
        <p
          id={noteId}
          className="k-field__note k-t-label-sm"
          role={error === undefined ? undefined : 'alert'}
        >
          {error ?? supporting}
        </p>
      ) : null}
      {open && wide ? (
        <ul
          ref={list}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={labelId}
          aria-activedescendant={`${listId}-${String(active)}`}
          className="k-select__menu"
          style={place}
          onKeyDown={onListKey}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-${String(index)}`}
              role="option"
              aria-selected={option.value === current}
              aria-disabled={option.disabled === true || undefined}
              data-active={index === active || undefined}
              className="k-select__option k-t-body-lg"
              onMouseEnter={() => enabled(index) && setActive(index)}
              onClick={() => enabled(index) && choose(option.value)}
            >
              <span className="k-select__check">
                {option.value === current ? <Icon icon={iconCheck} size={20} /> : null}
              </span>
              {option.label}
            </li>
          ))}
        </ul>
      ) : null}
      {!wide && open ? (
        <BottomSheet
          open={open}
          onClose={close}
          label={label}
          title={label}
          closeLabel={closeLabel}
        >
          <div className="form-stack">
            {options.length > SEARCH_FROM ? (
              <TextField
                label={searchLabel}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            ) : null}
            <div
              role="radiogroup"
              aria-label={label}
              className="k-select__rows"
              onKeyDown={(event) => {
                if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
                const rows = Array.from(
                  event.currentTarget.querySelectorAll<HTMLButtonElement>(
                    '[role="radio"]:not(:disabled)',
                  ),
                );
                const at = rows.findIndex((row) => row === document.activeElement);
                if (at < 0) return;
                event.preventDefault();
                rows[
                  (at + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length
                ]?.focus();
              }}
            >
              {shown.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={option.value === current}
                  disabled={option.disabled}
                  className="k-select__row k-layer k-t-body-lg"
                  onClick={() => choose(option.value)}
                >
                  <span className="k-select__check">
                    {option.value === current ? <Icon icon={iconCheck} size={20} /> : null}
                  </span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </BottomSheet>
      ) : null}
    </div>
  );
}
